import { useState } from 'react';
import { Modal } from './index';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { tenantsApi } from '../../services/api';
import './CreateTenantModal.css';

const CreateTenantModal = ({ isOpen, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    plan: 'starter',
    adminEmail: '',
    adminName: '',
  });
  const [features, setFeatures] = useState({
    emailPassword: true,
    magicLink: true,
    smsOtp: false,
    socialLogin: false,
    passkeys: false,
    fraudDetection: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (feature) => {
    setFeatures(prev => ({ ...prev, [feature]: !prev[feature] }));
  };

  const resetForm = () => {
    setFormData({ name: '', domain: '', plan: 'starter', adminEmail: '', adminName: '' });
    setFeatures({
      emailPassword: true,
      magicLink: true,
      smsOtp: false,
      socialLogin: false,
      passkeys: false,
      fraudDetection: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.name || !formData.domain || !formData.adminEmail) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        subdomain: formData.domain,
        plan: formData.plan,
        adminEmail: formData.adminEmail,
        adminName: formData.adminName,
        features: Object.entries(features)
          .filter(([, enabled]) => enabled)
          .map(([key]) => key),
      };

      const newTenant = await tenantsApi.create(payload);

      resetForm();

      if (onSuccess) {
        onSuccess(newTenant);
      } else {
        showToast(`Tenant "${formData.name}" created successfully!`, 'success');
        onClose();
      }
    } catch (err) {
      console.error('Failed to create tenant:', err);
      showToast(err.message || 'Failed to create tenant', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Tenant" size="md">
      <form onSubmit={handleSubmit} className="create-tenant-form">
        <div className="form-section">
          <h4 className="form-section-title">Tenant Information</h4>

          <div className="form-group">
            <label className="form-label">
              Tenant Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="form-input"
              placeholder="Acme Corporation"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Subdomain <span className="required">*</span>
            </label>
            <div className="input-with-suffix">
              <input
                type="text"
                name="domain"
                className="form-input"
                placeholder="acme"
                value={formData.domain}
                onChange={handleChange}
              />
              <span className="input-suffix">.auth1.com</span>
            </div>
            <p className="form-hint">This will be the tenant's login URL</p>
          </div>

          <div className="form-group">
            <label className="form-label">Plan</label>
            <select
              name="plan"
              className="form-select"
              value={formData.plan}
              onChange={handleChange}
            >
              <option value="starter">Starter - $29/mo</option>
              <option value="professional">Professional - $99/mo</option>
              <option value="enterprise">Enterprise - $299/mo</option>
              <option value="custom">Custom - Contact Sales</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h4 className="form-section-title">Admin Account</h4>

          <div className="form-group">
            <label className="form-label">
              Admin Email <span className="required">*</span>
            </label>
            <input
              type="email"
              name="adminEmail"
              className="form-input"
              placeholder="admin@acme.com"
              value={formData.adminEmail}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Admin Name</label>
            <input
              type="text"
              name="adminName"
              className="form-input"
              placeholder="John Smith"
              value={formData.adminName}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section features-section">
          <h4 className="form-section-title">Initial Features</h4>
          <div className="feature-toggles">
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.emailPassword}
                onChange={() => handleFeatureChange('emailPassword')}
              />
              <span>Email/Password Auth</span>
            </label>
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.magicLink}
                onChange={() => handleFeatureChange('magicLink')}
              />
              <span>Magic Link</span>
            </label>
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.smsOtp}
                onChange={() => handleFeatureChange('smsOtp')}
              />
              <span>SMS OTP</span>
            </label>
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.socialLogin}
                onChange={() => handleFeatureChange('socialLogin')}
              />
              <span>Social Login</span>
            </label>
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.passkeys}
                onChange={() => handleFeatureChange('passkeys')}
              />
              <span>Passkeys</span>
            </label>
            <label className="feature-toggle">
              <input
                type="checkbox"
                checked={features.fraudDetection}
                onChange={() => handleFeatureChange('fraudDetection')}
              />
              <span>Fraud Detection</span>
            </label>
          </div>
        </div>

        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTenantModal;
